import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Crown, Check, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREE_FEATURES = [
  'Up to 5 links',
  '2 themes (Obsidian, Paper)',
  'Basic analytics',
  'Contact collection',
  'Plexata branding'
];

const PRO_FEATURES = [
  'Unlimited links',
  'All premium themes',
  'Advanced analytics',
  'Contact export',
  'No Plexata branding',
  'Custom colors (coming soon)',
  'Priority support'
];

const FEATURE_COMPARISON = [
  { feature: 'Links', free: '5', pro: 'Unlimited' },
  { feature: 'Themes', free: '2', pro: 'All' },
  { feature: 'Analytics', free: 'Basic', pro: 'Advanced' },
  { feature: 'Contact Export', free: false, pro: true },
  { feature: 'Remove Branding', free: false, pro: true },
  { feature: 'Custom Colors', free: false, pro: 'Coming Soon' },
];

const PlanTab = ({ profile, showMsg, loadProfile }) => {
  const [packages, setPackages] = useState([]);
  const [loadingPkg, setLoadingPkg] = useState(null);
  const isPro = profile.plan === 'pro';

  useEffect(() => {
    axios.get(`${API}/payments/packages`).then(r => setPackages(r.data.packages)).catch(() => {});
  }, []);

  const handleUpgrade = async (packageId) => {
    setLoadingPkg(packageId);
    try {
      const originUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });
      window.location.href = res.data.url;
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to start checkout');
      setLoadingPkg(null);
    }
  };

  return (
    <div data-testid="plan-tab">
      <h2 className="font-heading text-2xl font-normal text-gray-900 mb-2">Your Plan</h2>
      <p className="text-sm text-gray-500 mb-8">
        {isPro ? 'You have full access to all Pro features.' : 'Upgrade to unlock all features.'}
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Free Plan */}
        <div className={`bg-white border-2 rounded-2xl p-6 ${!isPro ? 'border-gray-900' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-medium text-gray-900">Free</h3>
            {!isPro && <span className="text-xs font-bold uppercase tracking-wider text-white bg-gray-900 px-2.5 py-1 rounded-full">Current</span>}
          </div>
          <p className="text-3xl font-heading font-normal text-gray-900 mb-6">$0<span className="text-sm text-gray-400 font-body">/forever</span></p>
          <ul className="space-y-3 mb-6">
            {FREE_FEATURES.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-gray-400" /> {f}
              </li>
            ))}
          </ul>
          {!isPro && (
            <button disabled className="w-full bg-gray-100 text-gray-500 py-3 rounded-lg font-semibold text-sm cursor-not-allowed">
              Current Plan
            </button>
          )}
        </div>

        {/* Pro Plan */}
        <div className={`bg-gradient-to-br from-indigo-600 to-purple-600 border-2 border-transparent rounded-2xl p-6 text-white relative overflow-hidden ${isPro ? 'ring-2 ring-indigo-300' : ''}`}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-300" />
                <h3 className="font-heading text-xl font-medium">Pro</h3>
              </div>
              {isPro && <span className="text-xs font-bold uppercase tracking-wider bg-white/20 px-2.5 py-1 rounded-full">Active</span>}
            </div>
            
            <div className="mb-6">
              {packages.map(pkg => (
                <div key={pkg.id} className="mb-2">
                  <span className="text-3xl font-heading font-normal">${pkg.amount}</span>
                  <span className="text-sm text-white/70">/{pkg.interval}</span>
                  {pkg.interval === 'year' && (
                    <span className="ml-2 text-xs bg-yellow-400/20 text-yellow-200 px-2 py-0.5 rounded-full">Save 27%</span>
                  )}
                </div>
              ))}
            </div>

            <ul className="space-y-3 mb-6">
              {PRO_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-white/90">
                  <Check className="w-4 h-4 text-yellow-300" /> {f}
                </li>
              ))}
            </ul>

            {isPro ? (
              <button disabled className="w-full bg-white/20 text-white py-3 rounded-lg font-semibold text-sm cursor-not-allowed">
                You're a Pro!
              </button>
            ) : (
              <div className="space-y-2">
                {packages.map(pkg => (
                  <button
                    key={pkg.id}
                    onClick={() => handleUpgrade(pkg.id)}
                    disabled={loadingPkg === pkg.id}
                    className={`w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                      pkg.interval === 'year' 
                        ? 'bg-white text-indigo-600 hover:bg-gray-100' 
                        : 'bg-white/20 text-white hover:bg-white/30'
                    } disabled:opacity-50`}
                    data-testid={`upgrade-${pkg.id}`}
                  >
                    {loadingPkg === pkg.id ? (
                      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        Upgrade {pkg.interval === 'year' ? 'Yearly' : 'Monthly'} - ${pkg.amount}
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feature Comparison */}
      <div className="mt-12">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Feature Comparison</h3>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 font-semibold text-gray-900">Feature</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-900">Free</th>
                <th className="text-center px-4 py-3 font-semibold text-indigo-600">Pro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {FEATURE_COMPARISON.map((row, i) => (
                <tr key={i}>
                  <td className="px-4 py-3 text-gray-600">{row.feature}</td>
                  <td className="text-center px-4 py-3">
                    {typeof row.free === 'boolean' 
                      ? (row.free ? <Check className="w-4 h-4 text-green-500 mx-auto" /> : <span className="text-gray-300">-</span>)
                      : <span className="text-gray-600">{row.free}</span>
                    }
                  </td>
                  <td className="text-center px-4 py-3">
                    {typeof row.pro === 'boolean' 
                      ? (row.pro ? <Check className="w-4 h-4 text-indigo-600 mx-auto" /> : <span className="text-gray-300">-</span>)
                      : <span className="text-indigo-600 font-medium">{row.pro}</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlanTab;
